import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { useToast } from "@/app/providers/ToastProvider";
import { signIn, signOut } from "@/features/auth/api/authApi";
import { getCurrentUser } from "@/features/auth/api/sessionApi";
import type { UserResponse } from "@/features/auth/types";
import { setUnauthorizedHandler } from "@/lib/apiClient";

type AuthContextValue = {
  user: UserResponse | null;
  isAuthenticated: boolean;
  /** True until the initial session check resolves. */
  isLoading: boolean;
  login: (organizationId: string, username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const toast = useToast();
  const [user, setUser] = useState<UserResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadSession = useCallback(async () => {
    setIsLoading(true);
    try {
      setUser(await getCurrentUser());
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  // A 401 on any later request means the session expired: drop it and let the
  // app fall back to the login screen, without reloading the page.
  useEffect(() => {
    setUnauthorizedHandler(() => setUser(null));
    return () => setUnauthorizedHandler(null);
  }, []);

  const login = useCallback(async (organizationId: string, username: string, password: string) => {
    await signIn({ organizationId, username, password });
    const currentUser = await getCurrentUser();
    setUser(currentUser);
  }, []);

  const logout = useCallback(async () => {
    try {
      await signOut();
      setUser(null);
    } catch {
      toast.error("La déconnexion a échoué. Votre session est encore active. Réessayez.");
    }
  }, [toast]);

  const value = useMemo<AuthContextValue>(
    () => ({ user, isAuthenticated: user !== null, isLoading, login, logout }),
    [user, isLoading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside an AuthProvider");
  }
  return context;
}
